import {
  PLACE_ID_PATTERN,
  RuteroTourAttributes,
} from '@bandido/shared/types';

import { RuteroMoney, RuteroPricing } from './queries';
import { PlaceRef } from './places';

/**
 * Una oferta de experiencia. OJO con el precio: el proveedor cotiza una oferta
 * POR FRANJA ETARIA (adulto, menor, infante, senior), así que `price` es por
 * pasajero de esa franja y NO cambia con los pax buscados — verificado en vivo
 * pidiendo 1, 2 y 4 pax. La UI tiene que decirlo, si no el asesor cotiza el
 * grupo entero al precio de una persona.
 */
export interface TourOffer {
  provider: string;
  provenance: string;
  /** Tours v2 puede descubrir el producto antes de conocer ningún importe. */
  price?: RuteroMoney;
  priceFrom?: RuteroMoney;
  pricing?: RuteroPricing;
  images?: string[];
  attributes?: RuteroTourAttributes;
  places?: { destination?: { name?: string } };
  requiredPassengerInfo?: unknown;
  recommendedPassengerInfo?: unknown;
  title?: string;
  name?: string;
  description?: string;
  category?: string;
  facets?: {
    category?: { id?: string; name: string };
    languages?: { code?: string; name: string }[];
    duration?: { minutes?: number; days?: number };
  };
  productRef?: string;
  startDate?: string;
  startTime?: string;
  durationDays?: number;
  durationMinutes?: number;
  duration?: { minutes?: number; days?: number };
  ageFrom?: number;
  ageTo?: number;
  fareBand?: string;
  variant?: string;
  languages?: string[];
  pricePerPassenger?: boolean;
  priceIsIndicative?: boolean;
  [key: string]: unknown;
}

/** Una salida: la misma experiencia en una fecha, con sus tarifas por franja. */
export interface TourDeparture {
  date: string;
  fares: TourOffer[];
}

export interface TourLanguageFacet {
  code?: string;
  name: string;
}

export type SellableTourOffer = TourOffer & {
  title: string;
  productRef: string;
  priceFrom: RuteroMoney;
  pricing: RuteroPricing;
  images: [string, ...string[]];
};

/** La experiencia agrupada: una card por producto, no una por combinación. */
export interface TourProduct {
  /** Id estable del proveedor, cuando lo publica. */
  productRef: string;
  title: string;
  description?: string;
  category?: string;
  image: string;
  images: string[];
  location?: string;
  attributes?: RuteroTourAttributes;
  durationDays?: number;
  durationMinutes?: number;
  provider: string;
  languages?: string[];
  /** Identidad canónica del filtro; `name` queda solo como fallback visible. */
  languageFacets: TourLanguageFacet[];
  /** Todos los nombres publicados, incluso variantes del mismo código. */
  languageAliases: TourLanguageFacet[];
  departures: TourDeparture[];
  /** Oferta opaca que abre detail/options para este producto. */
  offer: SellableTourOffer;
  /** Tarifa de referencia (adulto) más barata, para el "desde" de la card. */
  /**
   * Precio de referencia COMPLETO (neto y final), no solo el importe de venta:
   * la card lo pasa por `PriceBlock`, que es lo único que decide si el asesor
   * ve el margen. Con solo el final, el neto no existía para el que vende.
   */
  fromPricing: RuteroPricing;
}

export interface TourPricePresentation {
  showFrom: boolean;
  unit: 'perPassenger' | 'perGroup' | null;
}

/**
 * Los calificadores visibles salen únicamente de las señales del contrato.
 * Ausente nunca significa por pasajero ni precio indicativo.
 */
export function tourPricePresentation(
  offer: Pick<TourOffer, 'priceIsIndicative' | 'pricePerPassenger'>,
): TourPricePresentation {
  return {
    showFrom: offer.priceIsIndicative === true,
    unit:
      offer.pricePerPassenger === true
        ? 'perPassenger'
        : offer.pricePerPassenger === false
          ? 'perGroup'
          : null,
  };
}

/** Edad con la que se decide cuál es la tarifa "de adulto" de referencia. */
export const TOUR_ADULT_REFERENCE_AGE = 30;

export function coversAge(offer: TourOffer, age: number): boolean {
  const { ageFrom, ageTo } = offer;
  if (typeof ageFrom !== 'number' && typeof ageTo !== 'number') return true;
  if (typeof ageFrom === 'number' && age < ageFrom) return false;
  if (typeof ageTo === 'number' && age > ageTo) return false;
  return true;
}

export type PricedTourOffer = TourOffer & {
  price: RuteroMoney;
  pricing: RuteroPricing;
};

export function isPricedTourOffer(offer: TourOffer): offer is PricedTourOffer {
  return Boolean(offer.price && offer.pricing);
}

/** La UI no convierte una respuesta incompleta en una card vendible. */
export function isSellableTourOffer(
  offer: TourOffer,
): offer is SellableTourOffer {
  const money = offer.priceFrom;
  const final = offer.pricing?.final;
  return Boolean(
    offer.provider &&
      offer.provenance &&
      offer.title &&
      offer.productRef &&
      offer.images?.[0] &&
      money &&
      Number.isInteger(money.amountMinor) &&
      money.amountMinor >= 0 &&
      /^[A-Z]{3}$/.test(money.currency) &&
      final &&
      Number.isInteger(final.amountMinor) &&
      final.amountMinor >= 0 &&
      /^[A-Z]{3}$/.test(final.currency),
  );
}

/**
 * Rango de edades de la franja, para cuando el proveedor NO publica el rótulo
 * (`fareBand`). El tope 999 del proveedor significa "sin límite", no 999 años.
 */
export function ageBandLabel(offer: TourOffer): string | null {
  const { ageFrom, ageTo } = offer;
  if (typeof ageFrom !== 'number') return null;
  if (typeof ageTo !== 'number' || ageTo >= 99) return `${ageFrom}+`;
  return `${ageFrom} - ${ageTo}`;
}

const byComparablePrice = (a: TourOffer, b: TourOffer): number => {
  const left = a.pricing?.final;
  const right = b.pricing?.final;
  if (!left || !right || left.currency !== right.currency) return 0;
  return left.amountMinor - right.amountMinor;
};

const normalizedLanguageCode = (code: string | undefined): string | undefined => {
  const normalized = code?.trim().toLowerCase();
  return normalized || undefined;
};

const normalizedLanguageName = (name: string): string => name.trim().toLocaleLowerCase();

const languageFacetIdentity = (facet: TourLanguageFacet): string =>
  normalizedLanguageCode(facet.code)
    ? `code:${normalizedLanguageCode(facet.code)}`
    : `name:${normalizedLanguageName(facet.name)}`;

function offerLanguageFacets(offer: TourOffer): TourLanguageFacet[] {
  const published = offer.facets?.languages;
  if (published !== undefined) {
    return published
      .filter((language) => Boolean(language.name.trim()))
      .map((language) => ({
        ...(normalizedLanguageCode(language.code)
          ? { code: normalizedLanguageCode(language.code) }
          : {}),
        name: language.name.trim(),
      }));
  }
  return (offer.languages ?? [])
    .filter((name) => Boolean(name.trim()))
    .map((name) => ({ name: name.trim() }));
}

function mergeLanguageFacets(
  current: readonly TourLanguageFacet[],
  incoming: readonly TourLanguageFacet[],
): TourLanguageFacet[] {
  const merged = new Map(
    current.map((facet) => [languageFacetIdentity(facet), facet]),
  );
  for (const facet of incoming) {
    const identity = languageFacetIdentity(facet);
    if (!merged.has(identity)) merged.set(identity, facet);
  }
  return [...merged.values()];
}

function mergePublishedLanguageAliases(
  current: readonly TourLanguageFacet[],
  incoming: readonly TourLanguageFacet[],
): TourLanguageFacet[] {
  const aliases = new Map(
    current.map((facet) => [
      `${normalizedLanguageCode(facet.code) ?? ''}:${normalizedLanguageName(facet.name)}`,
      facet,
    ]),
  );
  for (const facet of incoming) {
    const key = `${normalizedLanguageCode(facet.code) ?? ''}:${normalizedLanguageName(facet.name)}`;
    if (!aliases.has(key)) aliases.set(key, facet);
  }
  return [...aliases.values()];
}

/**
 * Agrupa las ofertas en productos. El proveedor devuelve el producto cartesiano
 * de fecha × franja etaria × variante (112 ofertas para UN tour de Madrid en
 * una semana), y pintarlas sueltas es una lista de cards idénticas.
 *
 * Se agrupa por `provider + productRef`: la referencia es estable pero su
 * namespace pertenece al proveedor. Sin ambas identidades la card no es
 * vendible; el título nunca sustituye un id ausente.
 *
 * Las fechas en que la actividad no opera ya no llegan: las filtra el hub por
 * CALENDARIO. Acá no se filtra por precio — una banda de infante vale 0 de
 * verdad y tiene que seguir apareciendo.
 */
export function groupTourProducts(offers: TourOffer[]): TourProduct[] {
  const products = new Map<string, TourProduct>();

  for (const offer of offers.filter(isSellableTourOffer)) {
    const { title } = offer;
    const key = `${offer.provider}:${offer.productRef}`;
    const product = products.get(key) ?? {
      productRef: offer.productRef,
      title,
      description: offer.description,
      languages:
        offer.facets?.languages?.map((language) => language.name) ??
        offer.languages,
      languageFacets: offerLanguageFacets(offer),
      languageAliases: offerLanguageFacets(offer),
      category: offer.facets?.category?.name ?? offer.category,
      image: offer.images[0],
      images: offer.images,
      location: offer.places?.destination?.name,
      attributes: offer.attributes,
      durationDays:
        offer.facets?.duration?.days ??
        offer.duration?.days ??
        offer.durationDays,
      durationMinutes:
        offer.facets?.duration?.minutes ??
        offer.duration?.minutes ??
        offer.durationMinutes,
      provider: offer.provider,
      departures: [],
      offer,
      fromPricing: offer.pricing,
    };

    product.languageFacets = mergeLanguageFacets(
      product.languageFacets,
      offerLanguageFacets(offer),
    );
    product.languageAliases = mergePublishedLanguageAliases(
      product.languageAliases,
      offerLanguageFacets(offer),
    );
    product.languages = product.languageFacets.map((language) => language.name);

    const date = offer.startDate ?? '';
    const departure = product.departures.find((item) => item.date === date);
    if (departure) departure.fares.push(offer);
    else product.departures.push({ date, fares: [offer] });

    products.set(key, product);
  }

  return [...products.values()]
    .map((product) => {
      const departures = product.departures
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((departure) => ({
          ...departure,
          // Por franja (los chicos primero) y, dentro de la franja, la más
          // barata arriba: el proveedor publica más de una tarifa por franja
          // sin ninguna forma de distinguirlas.
          fares: [...departure.fares].sort(
            (a, b) =>
              (a.ageFrom ?? 0) - (b.ageFrom ?? 0) ||
              byComparablePrice(a, b),
          ),
        }));
      // `priceFrom` ya es la autoridad comercial del search. Solo elegimos el
      // menor de varias cards del mismo producto; nunca inferimos una franja
      // adulta ni multiplicamos el importe por pasajeros.
      const referenceCurrency = product.offer.pricing.final.currency;
      const reference = ([
        ...departures
          .flatMap((departure) => departure.fares)
          .filter(
            (fare) => fare.pricing?.final.currency === referenceCurrency,
          ),
      ].sort(byComparablePrice)[0] ?? product.offer) as SellableTourOffer;
      return {
        ...product,
        departures,
        offer: reference,
        fromPricing: reference.pricing,
      };
    })
    .filter((product) => product.departures.length > 0);
}

export interface TourProductFilters {
  providers: string[];
  categories: string[];
  languages: string[];
}

export const EMPTY_TOUR_PRODUCT_FILTERS: TourProductFilters = {
  providers: [],
  categories: [],
  languages: [],
};

export type TourProductSort = 'relevance' | 'title' | 'price';

export interface TourLanguageOption {
  /** Código contractual o, sin código, el nombre exacto publicado. */
  value: string;
  label: string;
}

function languageAliases(products: readonly TourProduct[]): {
  codes: Map<string, string>;
  names: Map<string, Set<string>>;
} {
  const codes = new Map<string, string>();
  const names = new Map<string, Set<string>>();
  for (const facet of products.flatMap((product) => product.languageAliases)) {
    const identity = languageFacetIdentity(facet);
    const code = normalizedLanguageCode(facet.code);
    if (code) codes.set(code, identity);
    const name = normalizedLanguageName(facet.name);
    const identities = names.get(name) ?? new Set<string>();
    identities.add(identity);
    names.set(name, identities);
  }
  return { codes, names };
}

/** Convierte URLs legacy por nombre al código publicado, sin heurísticas. */
export function canonicalTourLanguageFilters(
  products: readonly TourProduct[],
  filters: readonly string[],
): string[] {
  const aliases = languageAliases(products);
  return [
    ...new Set(filters.flatMap((value) => {
      const normalized = normalizedLanguageName(value);
      if (normalized.startsWith('code:') || normalized.startsWith('name:')) {
        return [normalized];
      }
      const coded = aliases.codes.get(normalized);
      if (coded) return [coded];
      const named = aliases.names.get(normalized);
      return named ? [...named] : [`name:${normalized}`];
    })),
  ];
}

export function toggleTourLanguageFilter(
  products: readonly TourProduct[],
  filters: readonly string[],
  identity: string,
): string[] {
  const current = canonicalTourLanguageFilters(products, filters);
  return current.includes(identity)
    ? current.filter((value) => value !== identity)
    : [...current, identity];
}

function localizedLanguageName(code: string, locale: string): string | undefined {
  try {
    const label = new Intl.DisplayNames([locale || 'es'], { type: 'language' }).of(code);
    if (!label) return undefined;
    return `${label.charAt(0).toLocaleUpperCase(locale || 'es')}${label.slice(1)}`;
  } catch {
    return undefined;
  }
}

export function tourLanguageOptions(
  products: readonly TourProduct[],
  locale: string,
): TourLanguageOption[] {
  const facets = new Map<string, TourLanguageFacet>();
  for (const facet of products.flatMap((product) => product.languageFacets)) {
    const identity = languageFacetIdentity(facet);
    if (!facets.has(identity)) facets.set(identity, facet);
  }
  return [...facets.entries()]
    .map(([identity, facet]) => {
      const code = normalizedLanguageCode(facet.code);
      return {
        value: identity,
        label: code
          ? localizedLanguageName(code, locale) ?? facet.name
          : facet.name,
        identity,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label, locale))
    .map(({ identity: _identity, ...option }) => option);
}

/** Facetas normalizadas publicadas por Rutero; los grupos combinan con AND. */
export function filterTourProducts(
  products: TourProduct[],
  filters: TourProductFilters,
): TourProduct[] {
  const selectedLanguageIdentities = canonicalTourLanguageFilters(
    products,
    filters.languages,
  );
  return products.filter(
    (product) =>
      (!filters.providers.length || filters.providers.includes(product.provider)) &&
      (!filters.categories.length ||
        Boolean(product.category && filters.categories.includes(product.category))) &&
      (!selectedLanguageIdentities.length ||
        product.languageFacets.some((language) =>
          selectedLanguageIdentities.includes(languageFacetIdentity(language)))),
  );
}

/** Monedas realmente comparables del precio visible; sin importe no aporta una. */
export function tourProductCurrencies(products: TourProduct[]): string[] {
  return [
    ...new Set(
      products
        .map((product) => product.fromPricing?.final.currency)
        .filter((currency): currency is string => Boolean(currency)),
    ),
  ];
}

export function sortTourProducts(
  products: TourProduct[],
  sort: TourProductSort,
): TourProduct[] {
  if (sort === 'relevance') return products;
  if (sort === 'title') {
    return [...products].sort((left, right) => left.title.localeCompare(right.title));
  }
  // Montos de monedas distintas no tienen orden comercial común. En ese caso
  // se conserva la relevancia del hub en vez de fabricar una comparación FX.
  if (tourProductCurrencies(products).length > 1) return products;
  return [...products].sort(
    (left, right) =>
      left.fromPricing.final.amountMinor - right.fromPricing.final.amountMinor,
  );
}

export function tourProductFacetCount(
  products: TourProduct[],
  filters: TourProductFilters,
  patch: Partial<TourProductFilters>,
): number {
  return filterTourProducts(products, { ...filters, ...patch }).length;
}

export function tourProductCountsByProvider(
  products: TourProduct[],
  responded: readonly string[] = [],
): Record<string, number> {
  const counts = Object.fromEntries(responded.map((provider) => [provider, 0]));
  for (const product of products) {
    counts[product.provider] = (counts[product.provider] ?? 0) + 1;
  }
  return counts;
}

/**
 * Tarifa de referencia: la de adulto más barata con precio. NO la más barata a
 * secas: los infantes van a 0 y "desde $0" es una mentira con forma de gancho.
 */
export function referenceFare(departures: TourDeparture[]): TourOffer | undefined {
  const adultFares = departures
    .flatMap((departure) => departure.fares)
    .filter(
      (fare) =>
        coversAge(fare, TOUR_ADULT_REFERENCE_AGE) &&
        (fare.pricing?.final.amountMinor ?? 0) > 0,
    );
  return [...adultFares].sort(byComparablePrice)[0];
}

/** "2026-09-13" → "13/09". El año va aparte: son todas del mismo rango. */
export function formatShortDate(value: string): string {
  const [, month, day] = value.split('-');
  return day ? `${day}/${month}` : value;
}

/**
 * "13/09" o "13/09 · 09:00". La hora solo sale cuando el proveedor la publica,
 * que es cuando ese día tiene una ÚNICA sesión: con varias la omite antes que
 * elegir una por su cuenta.
 */
export function departureLabel(departure: TourDeparture): string {
  const time = departure.fares.find((fare) => fare.startTime)?.startTime;
  const date = formatShortDate(departure.date);
  return time ? `${date} · ${time}` : date;
}

/**
 * Lugar (`plc_*`) reconstruido desde la URL. Una referencia no canónica se
 * descarta. `kind`/`breadcrumb` quedan
 * con el `name` como placeholder, igual que en rail — ninguno de los dos
 * se renderiza para el valor YA seleccionado (ver `PlaceAutocomplete.tsx`).
 */
export function tourPlaceFromParams(
  id: string | undefined,
  name: string | undefined,
): PlaceRef | null {
  if (!id || !PLACE_ID_PATTERN.test(id)) return null;
  const resolvedName = name ?? id;
  return { id, name: resolvedName, breadcrumb: resolvedName, kind: 'city' };
}
